import { Component, TemplateRef, ViewChild, AfterViewInit, OnDestroy } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { StorageService } from '../../services/storage/storage.service';
import { StorageKeys } from '../../shared/enums/storage.enum';
import { Location } from '@angular/common';
import { EsignOverlayService } from '../../services/esign-overlay/esign-overlay.service';
import { EsignService } from '../../services/esign-service/esign.service';
import { PublicEsignService } from '../../services/public-esign/public-esign.service';
import { AuthService } from '../../services/auth/auth.service';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { faEye, faSignature } from '@fortawesome/free-solid-svg-icons';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-public-esign',
  standalone: false,
  templateUrl: './public-esign.component.html',
  styleUrls: ['./public-esign.component.scss']
})
export class PublicEsignComponent implements AfterViewInit, OnDestroy {
  faEye = faEye;
  faSignature = faSignature;
  activeTab: 'signed' | 'pending' = 'signed';
  signedDocs: any[] = [];
  signedTotalCount: number = 0;
  signedPage: number = 0;
  loadSignedRecords: boolean = false;
  signedErrorMessage: string = '';
  signedCountErrorMessage: string = '';
  pendingDocs: any[] = [];
  pendingTotalCount: number = 0;
  pendingPage: number = 0;
  loadPendingRecords: boolean = false;
  pendingErrorMessage: string = '';
  pendingCountErrorMessage: string = '';
  showAddCreditsModal = false;
  showSuccessToaster = false;
  showErrorToaster = false;
  successMessage = '';
  errorMessage = '';
  loadingDocId: string | null = null;
  selectedDocument: any = null;
  selectedDocumentUrl: SafeResourceUrl | null = null;
  documentModalRef?: NgbModalRef;
  showTrackModal = false;
  trackDoc: any = null;
  currentUserEmail: string = '';
  @ViewChild('documentModal') documentModalTpl!: TemplateRef<any>;
  private esignSuccessSub!: Subscription;

  constructor(
    private readonly storageService: StorageService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly sanitizer: DomSanitizer,
    private readonly modalService: NgbModal,
    private readonly location: Location,
    private readonly esignOverlayService: EsignOverlayService,
    private readonly eSignService: EsignService,
    private readonly publicEsignService: PublicEsignService,
    private readonly authService: AuthService,
  ) {}

  ngOnInit(): void {
    this.route.queryParams.subscribe((params) => {
      if (params['token']) {
        try {
          const decoded = JSON.parse(atob(params['token']));
          this.storageService.setDataToLocalStorage(StorageKeys.ACCESS_TOKEN, decoded.token);
        } catch (e) {
          console.error('Invalid token received:', e);
        }
      }
      this.location.replaceState(this.router.url?.split('?')[0]);
    });
    this.getSignedList(this.signedPage);
    this.getSignedCount();
    this.getPendingCount();
    this.fetchCredits();
    this.currentUserEmail = this.storageService.getDataFromLocalStorage(StorageKeys.SELF_DETAILS)?.emailAddress || '';

    this.esignSuccessSub = this.esignOverlayService.onSuccess$.subscribe(() => {
      this.pendingPage = 0;
      this.getPendingList(this.pendingPage);
      this.getPendingCount();
      this.getSignedList(this.signedPage);
      this.getSignedCount();
      this.fetchCredits();
    });
  }

  ngAfterViewInit(): void {
    if (!this.documentModalTpl) {
      console.warn('Document modal template not found');
    }
  }

  ngOnDestroy(): void {
    this.esignSuccessSub?.unsubscribe();
  }

  setTab(tab: 'signed' | 'pending'): void {
    if (this.activeTab === tab) return;
    this.activeTab = tab;
    if (tab === 'signed') {
      this.signedPage = 0;
      this.getSignedList(this.signedPage);
      this.getSignedCount();
    } else if (tab === 'pending') {
      this.pendingPage = 0;
      this.getPendingList(this.pendingPage);
      this.getPendingCount();
    }
  }

  onSignedPageChange(event: any): void {
    this.signedPage = event.page;
    this.getSignedList(this.signedPage);
  }

  onPendingPageChange(event: any): void {
    this.pendingPage = event.page;
    this.getPendingList(this.pendingPage);
  }

  getSignedList(page: number = 0): void {
    this.loadSignedRecords = true;
    this.signedErrorMessage = '';
    this.eSignService.getLogs(page).subscribe({
      next: (res: any) => {
        this.signedDocs = res?.data || [];
        this.loadSignedRecords = false;
      },
      error: (error: any) => {
        this.loadSignedRecords = false;
        this.signedErrorMessage = error?.error?.error?.message || 'Failed to fetch signed documents';
      }
    });
  }

  getSignedCount(): void {
    this.eSignService.getLogsCount().subscribe({
      next: (res: any) => {
        this.signedTotalCount = res?.data || 0;
      },
      error: (error: any) => {
        console.error('Failed to fetch signed count:', error);
      }
    });
  }

  getPendingList(page: number = 0): void {
    this.loadPendingRecords = true;
    this.pendingErrorMessage = '';
    this.publicEsignService.getPendingList(page).subscribe({
      next: (res: any) => {
        this.pendingDocs = res?.data || [];
        this.loadPendingRecords = false;
      },
      error: (error: any) => {
        this.loadPendingRecords = false;
        this.pendingErrorMessage = error?.error?.error?.message || 'Failed to fetch pending documents';
      }
    });
  }

  getPendingCount(): void {
    this.publicEsignService.getPendingCount().subscribe({
      next: (res: any) => {
        this.pendingTotalCount = res?.data?.count || 0;
      },
      error: (error: any) => {
        console.error('Failed to fetch pending count:', error);
      }
    });
  }

  fetchCredits(): void {
    this.authService.triggerCreditsRefresh();
  }

  openEsignOverlayForPending(doc: any): void {
    this.esignOverlayService.open(doc);
  }

  isOwner(doc: any): boolean {
    const sharedByEmail: string = doc?.sharedBy?.emailAddress || '';
    return sharedByEmail.toLowerCase() === this.currentUserEmail?.toLowerCase();
  }

  openTrackModal(doc: any): void {
    this.trackDoc = doc;
    this.showTrackModal = true;
  }

  closeTrackModal(): void {
    this.showTrackModal = false;
    this.trackDoc = null;
  }

  closeToaster(type: 'success' | 'error'): void {
    if (type === 'success') { this.showSuccessToaster = false; this.successMessage = ''; }
    else { this.showErrorToaster = false; this.errorMessage = ''; }
  }

  getStatus(status: string): string {
    const statusMap: { [key: string]: string } = {
      in_progress: 'Pending',
      completed: 'Completed',
      shared: 'Shared'
    };

    return statusMap[status] || status || '—';
  }

  openDocumentModal(doc: any) {
    if (!this.documentModalTpl) {
      console.error('Document modal template not initialized');
      return;
    }
    this.loadingDocId = doc?._id;
    this.eSignService.getDocument(doc?._id).subscribe({
      next: (res: any) => {
        const docUrl = res?.data;
        if (!docUrl) {
          this.loadingDocId = null;
          return;
        }
        this.selectedDocument = doc;
        this.selectedDocumentUrl = this.sanitizer.bypassSecurityTrustResourceUrl(docUrl);
        this.documentModalRef = this.modalService.open(this.documentModalTpl, {
          backdrop: 'static',
          size: 'xl',
          centered: true,
          scrollable: true
        });
        this.documentModalRef.result.catch(() => {
          this.loadingDocId = null;
          this.selectedDocument = null;
          this.selectedDocumentUrl = null;
        });
      },
      error: (error: any) => {
        this.loadingDocId = null;
        console.error('Failed to load document.', error);
      }
    });
  }

  openPendingDocumentModal(doc: any): void {
    if (!this.documentModalTpl) {
      console.error('Document modal template not initialized');
      return;
    }
    this.selectedDocument = doc;
    this.selectedDocumentUrl = this.sanitizer.bypassSecurityTrustResourceUrl(doc.lastSignedDocUrl);
    this.documentModalRef = this.modalService.open(this.documentModalTpl, {
      backdrop: 'static',
      size: 'xl',
      centered: true,
      scrollable: true
    });
    this.documentModalRef.result.catch(() => {
      this.selectedDocument = null;
      this.selectedDocumentUrl = null;
    });
  }

  closeDocumentModal() {
    this.loadingDocId = null;
    if (this.documentModalRef) {
      this.documentModalRef.close();
      this.documentModalRef = undefined;
    }
    this.selectedDocument = null;
    this.selectedDocumentUrl = null;
  }
}
